/** @format */

import React, { Suspense, useEffect, useState } from 'react';
import { useAuthStore } from '../../../context/useAuthStore';
import toast from 'react-hot-toast';
import axios from 'axios';
import {
	Chart as ChartJS,
	BarElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
} from 'chart.js';
import DashboardCountCard from '../../../components/ui/DashboardCountCard';
import CollegeWiseStudentDataModal from '../../../components/ui/CollegeWiseStudentDataModal';
import AllCollegeDataModal from '../../../components/ui/AllCollegeDataModal';
import SelectInput from '../../../components/ui/SelectInput';
const Bar = React.lazy(() =>
	import('react-chartjs-2').then((module) => ({ default: module.Bar }))
);

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const tableHeaders = [
	'Sl.No',
	'College Name',
	'Registered Students',
	'Subscribed Students',
	'Subscribed Subject',
];

type ChartDate = {
	raw: string;
	label: string;
};

type DashboardData = {
	totalStudents: number;
	totalColleges: number;
	totalRegistredSubjects: number;
	collegeWiseStudents: {
		collegeName: string;
		studentCount: number;
		totalSubjectsSubscribed: number;
		totalSubscriptions: number;
	}[];
	dailyNewUsers: {
		newUsers: number;
		registrationDate: string;
	}[];
	dailyVerifiedUsers: {
		verifiedUsers: number;
		verificationDate: string;
	}[];
	dailyRegistredCounts: {
		registredDate: string;
		registredUsers: number;
	}[];
};

const SuperAdminDashboardPage: React.FC = () => {
	const { user, token } = useAuthStore();
	const [loading, setLoading] = useState(true);
	const [dashboardData, setDashboardData] = useState<DashboardData | null>(
		null
	);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isAllCollegeModalOpen, setIsAllCollegeModalOpen] = useState(false);
	const [dateRange, setDateRange] = useState('last_7_days');

	const [modalData, setModalData] = useState<{
		collegeName: string;
	} | null>(null);

	const openModal = (collegeName: string) => {
		setModalData({ collegeName });
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setModalData(null);
		// fetchStudents();
	};

	useEffect(() => {
		const fetchDashboardData = async () => {
			if (!user?.userId) {
				toast.error('User not found.');
				setLoading(false);
				return;
			}

			try {
				const res = await axios.get<{ status: string; success: DashboardData }>(
					`${API_BASE_URL}/dashboard`,
					{
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${token}`,
						},
						withCredentials: true,
					}
				);

				if (
					res.data?.status === 'success' &&
					typeof res.data.success === 'object'
				) {
					setDashboardData(res.data.success);
				} else {
					toast.error('Invalid data format received.');
				}
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
			} catch (error) {
				toast.error('Failed to fetch dashboard data.');
			} finally {
				setLoading(false);
			}
		};

		fetchDashboardData();
	}, [token, user?.userId]);

	if (loading)
		return (
			<div className='flex justify-center items-center h-[calc(100vh-theme(spacing.20))] text-2xl font-semibold'>
				Loading...
			</div>
		);

	const getChartDates = (days: number): ChartDate[] => {
		const result: ChartDate[] = [];
		const today = new Date();
		const monthNames = [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December',
		];

		for (let i = days - 1; i >= 0; i--) {
			const date = new Date(today);
			date.setDate(today.getDate() - i);

			const raw = date.toISOString().split('T')[0];
			const day = String(date.getDate()).padStart(2, '0');
			const monthName = monthNames[date.getMonth()];
			const label = `${day}-${monthName}`;

			result.push({ raw, label });
		}

		return result;
	};

	const getDaysFromRange = (range: string) => {
		switch (range) {
			case 'last_30_days':
				return 30;
			case 'today':
				return 1;
			default:
				return 7;
		}
	};

	const chartDates: ChartDate[] = getChartDates(getDaysFromRange(dateRange));

	const chartLabels = chartDates.map((d) => d.label);
	const dateKeys = chartDates.map((d) => d.raw);

	const formatDataToMap = (
		dataArray: any[],
		dateKey: string,
		valueKey: string
	): Record<string, number> => {
		return dataArray.reduce((acc, item) => {
			acc[item[dateKey]] = item[valueKey];
			return acc;
		}, {} as Record<string, number>);
	};

	// Prepare bar chart data
	const newUsersMap = formatDataToMap(
		dashboardData?.dailyNewUsers || [],
		'registrationDate',
		'newUsers'
	);
	const verifiedUsersMap = formatDataToMap(
		dashboardData?.dailyVerifiedUsers || [],
		'verificationDate',
		'verifiedUsers'
	);
	// const registeredUsersMap = formatDataToMap(
	// 	dashboardData?.dailyRegistredCounts || [],
	// 	'registredDate',
	// 	'registredUsers'
	// );

	const barChartData = {
		labels: chartLabels,
		datasets: [
			{
				label: 'New Users',
				data: dateKeys.map((date) => newUsersMap[date] || 0),
				backgroundColor: '#810851',
			},
			{
				label: 'Subscribed Subjects',
				data: dateKeys.map((date) => verifiedUsersMap[date] || 0),
				backgroundColor: '#063656',
			},
			// {
			// 	label: 'Registered Subjects',
			// 	data: dateKeys.map((date) => registeredUsersMap[date] || 0),
			// 	backgroundColor: '#ffcf56',
			// },
		],
	};

	const allValues = barChartData.datasets.flatMap((ds) => ds.data);
	const maxValue = Math.max(...allValues);
	const maxTicks = Math.ceil((maxValue + 1) / 5) * 5;

	const barChartOptions = {
		responsive: true,
		plugins: {
			legend: {
				position: 'top' as const,
			},
			tooltip: { mode: 'index' as const, intersect: false },
		},
		scales: {
			x: {
				beginAtZero: true,
			},
			y: {
				beginAtZero: true,
				max: maxTicks,
				ticks: {
					stepSize: 5,
					precision: 0,
				},
			},
		},
	};

	const uniqueCollegeCount = new Set(
		dashboardData?.collegeWiseStudents.map((c) => c.collegeName)
	).size;
	const totalVerifiedUsers = dashboardData?.dailyVerifiedUsers.reduce(
		(sum, u) => sum + u.verifiedUsers,
		0
	);

	const dateRangeLabel =
		dateRange === 'last_7_days'
			? 'Last 7 Days'
			: dateRange === 'last_30_days'
			? 'Last 30 Days'
			: 'Today';

	return (
		<>
			<section className='grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-4'>
				<div className='md:col-span-2 col-span-1'>
					<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
						{/* Total Colleges */}
						<DashboardCountCard
							title='Total Colleges'
							count={dashboardData?.totalColleges}
							onClick={() => setIsAllCollegeModalOpen(true)}
						/>
						{/* Registered Colleges */}
						<DashboardCountCard
							title='Registered Colleges'
							count={uniqueCollegeCount}
						/>
						{/* Total Students */}
						<DashboardCountCard
							title='Total Students'
							count={dashboardData?.totalStudents}
						/>
						{/* Subscribed Subjects */}
						<DashboardCountCard
							title='Subscribed Subjects'
							count={totalVerifiedUsers}
						/>
						{/* Bar Chart */}
						<section className='SuperAdminContainer md:col-span-2 col-span-1'>
							{/* Add Dropdown for date range */}
							<div className='flex items-center justify-between mb-4'>
								<h2 className='text-base font-bold mb-2'>
									User Activity ({dateRangeLabel})
								</h2>
								<SelectInput
									name='dateRange'
									label=''
									value={dateRange}
									onChange={(val) => setDateRange(val)}
									options={[
										{ label: 'Today', value: 'today' },
										{ label: 'Last 7 Days', value: 'last_7_days' },
										{ label: 'Last 30 Days', value: 'last_30_days' },
									]}
								/>
							</div>
							<Suspense fallback={<div>Loading chart...</div>}>
								<Bar
									data={barChartData}
									options={barChartOptions}
								/>
							</Suspense>
						</section>
					</div>
				</div>
				{/* College table */}
				<section className='subjectTableSection col-span-1 md:col-span-2'>
					<div className='overflow-x-auto'>
						<div className='max-h-[480px] overflow-y-auto'>
							<table className='min-w-full border-collapse'>
								<thead className='sticky top-0 bg-white z-10'>
									<tr className='tableHeadRow bg-gray-200'>
										{tableHeaders.map((header, index) => (
											<th
												key={index}
												className='tableHeadValue whitespace-normal  text-sm'>
												{header}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{dashboardData?.collegeWiseStudents.map((data, index) => (
										<tr
											key={index}
											className='tableBodyRow'>
											<td className='tableBodyValue text-xs'>{index + 1}</td>
											<td className='tableBodyValue text-xs'>
												{data.collegeName}
											</td>
											<td
												className='tableBodyValue text-xs cursor-pointer'
												onClick={() => openModal(data.collegeName)}>
												<span className='inline-flex items-center rounded-md bg-green-50 px-2.5 py-1 text-sm font-semibold text-green-600 inset-ring inset-ring-green-500/10'>
													{data.studentCount}
												</span>
											</td>
											<td className='tableBodyValue text-xs'>
												<span
													className={`inline-flex items-center rounded-md px-2.5 py-1 text-sm font-semibold inset-ring 
                          ${
														data.totalSubscriptions === 0
															? 'bg-red-50 text-red-600 inset-ring-red-500/10'
															: 'bg-green-50 text-green-600 inset-ring-green-500/10'
													}`}>
													{data.totalSubscriptions}
												</span>
											</td>
											<td className='tableBodyValue text-xs'>
												<span
													className={`inline-flex items-center rounded-md px-2.5 py-1 text-sm font-semibold inset-ring 
                          ${
														data.totalSubjectsSubscribed === 0
															? 'bg-red-50 text-red-600 inset-ring-red-500/10'
															: 'bg-green-50 text-green-600 inset-ring-green-500/10'
													}`}>
													{data.totalSubjectsSubscribed}
												</span>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</section>
			</section>
			{isModalOpen && modalData && (
				<CollegeWiseStudentDataModal
					collegeName={modalData.collegeName}
					onClose={closeModal}
				/>
			)}
			{isAllCollegeModalOpen && (
				<AllCollegeDataModal onClose={() => setIsAllCollegeModalOpen(false)} />
			)}
		</>
	);
};

export default SuperAdminDashboardPage;
